/**
 * Admin · verticals (GET/POST/PUT/DELETE /admin/verticals) — owner-only, surface "platform".
 *
 * A vertical IS the assessment vocabulary: the competencies an interview probes, their relative
 * weights, the rubric anchors a judge scores against, and the fallback questions used whenever
 * generation is unavailable. Editing here is the only way TalentHop learns a new industry — there
 * is no global question bank left to fall back on.
 *
 * The page owns the FULL definition. The Calibration layer owns only the serving weight override
 * per vertical (model_registry.metrics.weights) and links back here for everything else, so a
 * competency's label, anchors and questions have exactly one editor.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  COMPETENCY_KINDS,
  DEFAULT_VERTICAL_KEY,
  EXERCISE_TYPES,
  OPTION_LETTERS,
  VERTICAL_LIMITS,
  applyTier,
  choiceLengthSpread,
  correctIsLongestOption,
  deriveTier,
  normalisedWeights,
  validateVertical,
  type CompetencyExercise,
  type CompetencyKind,
  type CompetencyTier,
  type ExerciseCritique,
  type ExerciseType,
  type Vertical,
  type VerticalCompetency,
} from "@talenthop/shared";
import { TierToggle } from "../../components/TierToggle.js";
import { api, ApiError } from "../../lib/api.js";
import { Info, Alert, Plus, Trash, Bolt, Copy, ClipboardPaste } from "../../components/icons.js";
import { Switch, apiErrorText } from "./adminUi.js";

/**
 * `editions` is the list of version numbers that have a stored snapshot behind them
 * (vertical_editions, migration 0042). It is what makes the Edition column mean something: before
 * 0042 only the CURRENT competency set was ever kept, so a requisition pinned to "v2" of a
 * re-weighted vertical named an edition nobody could produce. Optional so an older API payload
 * simply shows no history rather than claiming a complete one.
 */
type AdminVertical = Vertical & { editions?: number[] };

interface VerticalsResponse {
  verticals: AdminVertical[];
}

/** The owner console reads EVERY vertical, active or not — /verticals (recruiter) hides inactive
 *  ones, and an owner who can't see a deactivated vertical can never reactivate it. */
export function useAdminVerticals(): {
  verticals: AdminVertical[];
  isLoading: boolean;
  isError: boolean;
} {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-verticals"],
    queryFn: () => api.get<VerticalsResponse>("/admin/verticals"),
  });
  return { verticals: data?.verticals ?? [], isLoading, isError };
}

/**
 * What a competency set must add up to before the API will publish it (`assertPublishable`). The
 * scorer itself normalises, so a set summing to 93 would still rank sanely — but then every
 * percentage this console shows differs from the weight that was typed in, which is exactly how an
 * owner comes to believe a rubric says something it doesn't.
 */
const REQUIRED_WEIGHT_SUM = 100;

/** An in-progress edit. `isNew` decides POST vs PUT; `isSystem`/`version` are server-owned and
 *  carried only so the editor can warn about them — neither is ever sent back. `baseline` is the
 *  saved competency set, used to predict whether this save bumps the edition. */
interface Draft {
  isNew: boolean;
  isSystem: boolean;
  version: number;
  key: string;
  name: string;
  description: string;
  isActive: boolean;
  sortOrder: number;
  competencies: VerticalCompetency[];
  /** Extra "never ask about this" clauses for this industry — see the editor's guardrail card. */
  protectedTopics: string[];
  baseline: VerticalCompetency[];
}

const KIND_LABEL: Record<CompetencyKind, string> = {
  behavioural: "Behavioural — probed through lived experience",
  practical: "Practical — probed by doing the work",
};

const BLANK_COMPETENCY: VerticalCompetency = {
  competencyId: "",
  label: "",
  weight: 20,
  kind: "behavioural",
  anchors: "0-40: ; 41-70: ; 71-100: ",
  questions: [""],
};

/** Owner-facing names for the artefact families (EXERCISE_TYPES). Same idiom as KIND_LABEL: the
 *  stored value is the discriminant the serving code switches on, the copy is what the select
 *  shows. "Artefact" leads with examples because it is the family most owner verticals want and
 *  the word alone doesn't say so. */
const EXERCISE_TYPE_LABEL: Record<ExerciseType, string> = {
  code: "Code — an excerpt of a program",
  artefact: "Artefact — a work document: a lesson plan, an email, a handover note",
};

/** A fresh critique variant: an artefact to show and a question to ask over it. No options by
 *  default — an open explain-aloud question is the base form; choose-then-explain is opt-in. */
const BLANK_CRITIQUE: ExerciseCritique = { snippet: "", prompt: "" };

function toDraft(v: Vertical): Draft {
  const comps = v.competencies.map((c) => ({ ...c, questions: [...c.questions] }));
  return {
    isNew: false,
    isSystem: v.isSystem,
    version: v.version,
    key: v.key,
    name: v.name,
    description: v.description,
    isActive: v.isActive,
    sortOrder: v.sortOrder,
    competencies: comps,
    protectedTopics: [...(v.protectedTopics ?? [])],
    baseline: comps,
  };
}

function blankDraft(sortOrder: number): Draft {
  return {
    isNew: true,
    isSystem: false,
    version: 1,
    key: "",
    name: "",
    description: "",
    isActive: true,
    sortOrder,
    competencies: [{ ...BLANK_COMPETENCY, questions: [""] }],
    // Empty is a safe start: the universal protected-attribute rule is in code and applies to
    // every vertical regardless. These clauses only ADD to it.
    protectedTopics: [],
    baseline: [],
  };
}

const weightSum = (comps: VerticalCompetency[]): number =>
  comps.reduce((a, c) => a + (Number.isFinite(c.weight) ? c.weight : 0), 0);

/**
 * Mirrors `scoringFingerprint` in apps/api/src/functions/adminVerticals.ts: the fields whose change
 * alters what a score MEANS. Fallback questions are deliberately excluded — they are what the
 * interview asks when generation is unavailable, not what the judge measures against — so an owner
 * improving a question set is not punished with an invalidated audit trail.
 *
 * The \u0000 separators must match the server's byte for byte. Joined bare, two edits could cancel
 * out — relabelling "Grade 1" to "Grade 11" while dropping the weight 12 to 2 fingerprinted
 * identically — so this banner promised "no version bump" on a real scoring change, and the
 * server agreed with it. Change this and the API copy together or neither.
 */
const scoringFingerprint = (comps: VerticalCompetency[]): string =>
  comps
    .map((c) =>
      [
        c.competencyId,
        c.label,
        c.weight,
        c.kind,
        c.anchors.trim(),
        // The practical exercise is IN: its hidden test cases are ground truth the judge weighs a
        // pass rate against, so swapping one changes what a score measured every bit as much as
        // re-wording an anchor does.
        JSON.stringify(c.exercise ?? null),
      ].join("\u0000"),
    )
    .sort()
    .join("\u0000");

/** Server messages here are written for the owner ("Switch it off instead") — never bury them. */
function verticalError(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === "object" && "message" in err.body) {
    const m = (err.body as { message?: unknown }).message;
    if (typeof m === "string" && m.length > 0 && m.length < 400) return m;
  }
  return apiErrorText(err, fallback);
}

/**
 * The JSON shape Copy/Paste round-trips — exactly the editable surface `save`'s mutationFn sends
 * over the wire (key, name, description, isActive, sortOrder, competencies, protectedTopics),
 * `key` included even for an existing vertical so a copy is a complete, self-documenting example —
 * paste just won't let a key change stick on anything but a brand-new draft (see applyPastedJson).
 * Never includes isSystem/version/baseline — those are server-owned or editor-internal.
 */
function editableJsonOf(d: Draft): Record<string, unknown> {
  return {
    key: d.key.trim(),
    name: d.name.trim(),
    description: d.description.trim(),
    isActive: d.isActive,
    sortOrder: d.sortOrder,
    competencies: d.competencies.map((c) => ({
      competencyId: c.competencyId.trim(),
      label: c.label.trim(),
      weight: c.weight,
      kind: c.kind,
      anchors: c.anchors.trim(),
      questions: c.questions.map((q) => q.trim()).filter((q) => q.length > 0),
      exercise: c.exercise ?? null,
    })),
    protectedTopics: d.protectedTopics.map((t) => t.trim()).filter((t) => t.length > 0),
  };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * Parses clipboard JSON into a competency/topic set and merges it into the CURRENT draft —
 * everything server-owned (isSystem, version) or that the form itself already disabled for an
 * existing vertical (key — the API refuses a rename) stays untouched no matter what the pasted
 * JSON says, so paste can never smuggle through what the form wouldn't let you type. `baseline` is
 * deliberately left alone too: it is what save's edition-bump banner compares against, and a paste
 * that changes the rubric SHOULD trip that banner exactly like typing the same change would.
 *
 * Defensive rather than a hard schema check — a hand-written or LLM-produced JSON blob is more
 * likely to have a missing field than a wrong type, and the existing `validateVertical` + weight-
 * sum problems banner already re-checks everything the moment this returns, in the owner's own
 * words. This only has to not crash and not silently drop a field that WAS there.
 */
function applyPastedJson(parsed: unknown, draft: Draft): Draft | { error: string } {
  if (!isRecord(parsed)) return { error: "That doesn't look like a vertical — expected a JSON object." };
  const rawComps = parsed.competencies;
  if (!Array.isArray(rawComps) || rawComps.length === 0) {
    return { error: "Missing or empty \"competencies\" array." };
  }
  const competencies: VerticalCompetency[] = [];
  for (const raw of rawComps) {
    if (!isRecord(raw)) return { error: "Each competency must be a JSON object." };
    const kind = raw.kind === "practical" ? "practical" : "behavioural";
    const questions = Array.isArray(raw.questions)
      ? raw.questions.filter((q): q is string => typeof q === "string" && q.trim().length > 0)
      : [];
    const exercise =
      raw.exercise && isRecord(raw.exercise) && typeof raw.exercise.type === "string"
        ? (raw.exercise as unknown as CompetencyExercise)
        : null;
    competencies.push({
      competencyId: typeof raw.competencyId === "string" ? raw.competencyId : "",
      label: typeof raw.label === "string" ? raw.label : "",
      weight: typeof raw.weight === "number" && Number.isFinite(raw.weight) ? Math.round(raw.weight) : 0,
      kind,
      anchors: typeof raw.anchors === "string" ? raw.anchors : "",
      questions: questions.length > 0 ? questions : [""],
      exercise,
    });
  }
  const protectedTopics = Array.isArray(parsed.protectedTopics)
    ? parsed.protectedTopics.filter((t): t is string => typeof t === "string")
    : draft.protectedTopics;

  return {
    ...draft,
    // A brand-new draft has no key yet and no audit trail to protect — paste may set it. An
    // existing vertical's key field is disabled in the form for the same reason the API refuses a
    // PUT that changes it (packages/db pins requisitions to it); paste must not do through the
    // back door what the form already blocks in front.
    key: draft.isNew && typeof parsed.key === "string" ? parsed.key : draft.key,
    name: typeof parsed.name === "string" ? parsed.name : draft.name,
    description: typeof parsed.description === "string" ? parsed.description : draft.description,
    isActive: typeof parsed.isActive === "boolean" ? parsed.isActive : draft.isActive,
    sortOrder:
      typeof parsed.sortOrder === "number" && Number.isFinite(parsed.sortOrder)
        ? Math.round(parsed.sortOrder)
        : draft.sortOrder,
    competencies,
    protectedTopics,
  };
}

export function Verticals(): ReactNode {
  const queryClient = useQueryClient();
  const { verticals, isLoading, isError } = useAdminVerticals();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["admin-verticals"] });
    // The recruiter-facing list is a different cache key and a different (active-only) payload —
    // without this a freshly deactivated vertical stays pickable in New Request until a reload.
    void queryClient.invalidateQueries({ queryKey: ["verticals"] });
  };

  const save = useMutation({
    mutationFn: (d: Draft) => {
      // The PUT route takes the key in the PATH and refuses it in the body — a key is a
      // requisition's audit anchor, so it is not renameable once anything has been assessed on it.
      const body = {
        name: d.name.trim(),
        description: d.description.trim(),
        isActive: d.isActive,
        sortOrder: d.sortOrder,
        competencies: d.competencies.map((c) => ({
          competencyId: c.competencyId.trim(),
          label: c.label.trim(),
          weight: c.weight,
          kind: c.kind,
          anchors: c.anchors.trim(),
          questions: c.questions.map((q) => q.trim()).filter((q) => q.length > 0),
          // Explicit — never omitted. On this wire OMITTED means "leave the stored exercise
          // alone" (the pre-editor clients withCarriedExercises still shields) and NULL means
          // "clear it". This editor shows the exercise, so it always says what it saw: the
          // draft's exercise verbatim — no trimming, because a byte-identical round-trip is
          // what keeps an untouched save from bumping the edition.
          exercise: c.exercise ?? null,
        })),
        protectedTopics: d.protectedTopics.map((t) => t.trim()).filter((t) => t.length > 0),
      };
      const key = d.key.trim();
      return d.isNew
        ? api.post<{ vertical: Vertical }>("/admin/verticals", { ...body, key })
        : api.put<{ vertical: Vertical }>(`/admin/verticals/${encodeURIComponent(key)}`, body);
    },
    onSuccess: () => {
      invalidate();
      setDraft(null);
    },
  });

  const remove = useMutation({
    mutationFn: (key: string) => api.del(`/admin/verticals/${encodeURIComponent(key)}`),
    onSuccess: () => {
      invalidate();
      setConfirmDelete(null);
    },
  });

  /** The list's on/off switch. Sends the vertical back unchanged apart from `isActive`, so it can
   *  never bump the edition — the scoring fingerprint is identical by construction.
   *
   *  `protectedTopics` MUST be echoed back with the rest: PUT replaces the whole editable surface
   *  and the field defaults to [] when absent, so omitting it here would silently erase a
   *  vertical's safeguarding clauses every time an owner flicked the switch. */
  const toggleActive = useMutation({
    mutationFn: (v: Vertical) =>
      api.put<{ vertical: Vertical }>(`/admin/verticals/${encodeURIComponent(v.key)}`, {
        name: v.name,
        description: v.description,
        isActive: !v.isActive,
        sortOrder: v.sortOrder,
        competencies: v.competencies,
        protectedTopics: v.protectedTopics ?? [],
      }),
    onSuccess: invalidate,
  });

  if (draft) {
    return (
      <VerticalEditor
        draft={draft}
        onChange={setDraft}
        onCancel={() => setDraft(null)}
        onSave={() => save.mutate(draft)}
        saving={save.isPending}
        error={save.isError ? verticalError(save.error, "Couldn't save this vertical. Please retry.") : null}
      />
    );
  }

  const nextSort = verticals.reduce((a, v) => Math.max(a, v.sortOrder), 0) + 10;

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Verticals</h1>
          <div className="sub">
            The assessment vocabulary behind every interview — one per family of roles
          </div>
        </div>
        <button className="btn primary" onClick={() => setDraft(blankDraft(nextSort))}>
          <Plus /> New vertical
        </button>
      </div>

      <div className="banner info" style={{ marginBottom: 18 }}>
        <Info />
        <div>
          <b>A vertical decides what "good" means for a family of roles.</b> A requisition picks one,
          and everything downstream — the questions asked, the rubric the judge scores against, the
          fallback set used when generation is unavailable — comes from it. There is no global
          question bank, so a teacher can never be asked to design an API rate limiter.
        </div>
      </div>

      {isLoading && <div className="card pad mut sm">Loading verticals…</div>}

      {isError && (
        <div className="banner crit">
          <Alert />
          <span>Couldn't load the verticals. Please retry in a moment.</span>
        </div>
      )}

      {!isLoading && !isError && (
        <div className="card">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Vertical</th>
                  <th style={{ textAlign: "center" }}>Competencies</th>
                  <th style={{ textAlign: "center" }}>Weight sum</th>
                  <th style={{ textAlign: "center" }}>Edition</th>
                  <th style={{ textAlign: "center" }}>Active</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {[...verticals]
                  .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
                  .map((v) => {
                    const sum = weightSum(v.competencies);
                    return (
                      <tr key={v.key}>
                        <td>
                          <div style={{ fontWeight: 650 }}>
                            {v.name}{" "}
                            {v.isSystem && (
                              <span className="pill n" style={{ fontSize: 12 }} title="Seeded by TalentHop — editable, but never deletable">
                                System
                              </span>
                            )}
                          </div>
                          <div className="mut sm" style={{ fontFamily: "var(--mono)", fontSize: 12 }}>
                            {v.key}
                          </div>
                        </td>
                        <td style={{ textAlign: "center" }} className="tnum">
                          {v.competencies.length}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span
                            className={sum === REQUIRED_WEIGHT_SUM ? "pill v" : "pill r"}
                            title={`Weights must total ${REQUIRED_WEIGHT_SUM} so each one reads directly as its share of the composite`}
                          >
                            {sum}
                          </span>
                        </td>
                        <td style={{ textAlign: "center" }} className="tnum">
                          {/* The edition, and how much of its history is actually reproducible.
                              An owner defending a disputed score needs the edition that score was
                              taken under, so "v4 · 4 kept" and "v4 · 1 kept" are very different
                              states and the console says which one this is. */}
                          v{v.version}
                          <div className="mut" style={{ fontSize: 12 }}>
                            {v.editions === undefined
                              ? "\u00a0"
                              : v.editions.length >= v.version
                                ? `${v.editions.length} kept`
                                : `${v.editions.length} of ${v.version} kept`}
                          </div>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {/* The default vertical is where every unresolvable requisition lands, so
                              switching it off would leave those with nowhere neutral to go. The API
                              refuses it; the switch shouldn't invite the attempt. */}
                          <div
                            style={{ display: "inline-flex" }}
                            title={
                              v.key === DEFAULT_VERTICAL_KEY
                                ? "The fallback every other vertical resolves down to — it always stays on"
                                : undefined
                            }
                          >
                            <Switch
                              on={v.isActive}
                              disabled={toggleActive.isPending || v.key === DEFAULT_VERTICAL_KEY}
                              onToggle={() => toggleActive.mutate(v)}
                            />
                          </div>
                        </td>
                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          <button className="btn sm" onClick={() => setDraft(toDraft(v))}>
                            Edit
                          </button>
                          {!v.isSystem && (
                            <button
                              className="btn sm ghost"
                              style={{ marginLeft: 6 }}
                              onClick={() => setConfirmDelete(v.key)}
                            >
                              <Trash />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(toggleActive.isError || remove.isError) && (
        <div className="banner crit" style={{ marginTop: 14 }}>
          <Alert />
          <span>
            {verticalError(
              toggleActive.error ?? remove.error,
              "That change didn't go through. Please retry.",
            )}
          </span>
        </div>
      )}

      {confirmDelete && (
        <div className="card pad" style={{ marginTop: 16, borderColor: "var(--crit)" }}>
          <div style={{ fontWeight: 650, marginBottom: 6 }}>Delete “{confirmDelete}”?</div>
          <p className="mut sm" style={{ margin: "0 0 12px" }}>
            This removes the definition outright. It only goes through if no requisition has ever
            been assessed under it — deleting one that has would strip the anchors a past score is
            defended with, so the server refuses and asks you to switch it off instead.
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn" onClick={() => setConfirmDelete(null)} disabled={remove.isPending}>
              Cancel
            </button>
            <button
              className="btn danger-solid"
              disabled={remove.isPending}
              onClick={() => remove.mutate(confirmDelete)}
            >
              {remove.isPending ? "Deleting…" : "Delete vertical"}
            </button>
          </div>
        </div>
      )}

      <div className="disclaimer">
        <b>Weights are relative — the scorer normalises them before anything is ranked.</b> They are
        still held to a total of {REQUIRED_WEIGHT_SUM} so that each number reads directly as the
        share of the composite it will become. The thing to change carefully is the anchors: they
        are the audit trail a disputed score is defended with, which is why re-wording one bumps the
        vertical's edition and requisitions pin the edition they were created under.
      </div>
    </section>
  );
}

/** Full-definition editor for one vertical. Nothing is sent until Save. */
function VerticalEditor({
  draft,
  onChange,
  onCancel,
  onSave,
  saving,
  error,
}: {
  draft: Draft;
  onChange: (d: Draft) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  error: string | null;
}): ReactNode {
  const set = (patch: Partial<Draft>): void => onChange({ ...draft, ...patch });
  const setComp = (i: number, patch: Partial<VerticalCompetency>): void =>
    set({ competencies: draft.competencies.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });

  // Low/Medium/High quick-set: rebalances every competency's weight from its tier (own change +
  // every sibling's currently-displayed tier), so the vertical editor's "must total 100" gate is
  // satisfied by construction — no "Even them out" click needed after using tiers.
  const setTier = (i: number, tier: CompetencyTier): void => {
    const weights = draft.competencies.map((c) => c.weight);
    const next = applyTier(weights, i, tier);
    set({ competencies: draft.competencies.map((c, idx) => ({ ...c, weight: next[idx] ?? c.weight })) });
  };

  // Copy/Paste JSON — round-trips the same editable shape `save` sends, so a whole vertical (or
  // one written by hand, or generated elsewhere) can be composed as one JSON blob instead of typed
  // field by field. Paste re-runs the SAME `problems` validation below before Save is ever enabled
  // — nothing pasted is trusted further than anything typed.
  const [jsonMsg, setJsonMsg] = useState<{ tone: "good" | "warn"; text: string } | null>(null);
  const copyJson = (): void => {
    void navigator.clipboard
      ?.writeText(JSON.stringify(editableJsonOf(draft), null, 2))
      .then(() => setJsonMsg({ tone: "good", text: "Copied this vertical as JSON." }))
      .catch(() => setJsonMsg({ tone: "warn", text: "Couldn't write to the clipboard." }));
  };
  const pasteJson = (): void => {
    void navigator.clipboard
      ?.readText()
      .then((text) => {
        let parsed: unknown;
        try {
          parsed = JSON.parse(text);
        } catch {
          setJsonMsg({ tone: "warn", text: "Clipboard doesn't contain valid JSON." });
          return;
        }
        const result = applyPastedJson(parsed, draft);
        if ("error" in result) {
          setJsonMsg({ tone: "warn", text: result.error });
          return;
        }
        onChange(result);
        setJsonMsg({ tone: "good", text: "Pasted — check Not ready to save yet below, then Save." });
      })
      .catch(() =>
        setJsonMsg({
          tone: "warn",
          text: "Couldn't read the clipboard — your browser may need permission, or nothing was copied.",
        }),
      );
  };

  const sum = weightSum(draft.competencies);
  const shares = normalisedWeights({ competencies: draft.competencies });
  const nameOk = draft.name.trim().length >= 2;
  // Every problem at once, in the owner's language — validateVertical is built for exactly this,
  // and the weight total is the one rule it can't know about (it belongs to the API's publish gate).
  const problems = [
    ...(nameOk ? [] : ["This vertical needs a name."]),
    ...validateVertical({
      key: draft.key,
      competencies: draft.competencies,
      // Blank rows are stripped on save, so an owner mid-typing isn't told off for an empty box.
      protectedTopics: draft.protectedTopics.filter((t) => t.trim().length > 0),
    }),
    ...(sum === REQUIRED_WEIGHT_SUM
      ? []
      : [
          sum < REQUIRED_WEIGHT_SUM
            ? `Weights add up to ${sum} — ${REQUIRED_WEIGHT_SUM - sum} short of ${REQUIRED_WEIGHT_SUM}.`
            : `Weights add up to ${sum} — ${sum - REQUIRED_WEIGHT_SUM} over ${REQUIRED_WEIGHT_SUM}.`,
        ]),
  ];
  const canSave = problems.length === 0 && !saving;
  // Mirrors the server rule: only a change to what the judge measures moves the edition.
  const willBump =
    !draft.isNew && scoringFingerprint(draft.baseline) !== scoringFingerprint(draft.competencies);

  /** Split the required total evenly, giving the remainder to the first rows. A hard total of 100
   *  otherwise makes "add a sixth competency" an arithmetic puzzle before it is a rubric decision. */
  const evenOutWeights = (): void => {
    const n = draft.competencies.length;
    if (n === 0) return;
    const base = Math.floor(REQUIRED_WEIGHT_SUM / n);
    const remainder = REQUIRED_WEIGHT_SUM - base * n;
    set({
      competencies: draft.competencies.map((c, i) => ({ ...c, weight: base + (i < remainder ? 1 : 0) })),
    });
  };

  const addCompetency = (): void => {
    if (draft.competencies.length >= VERTICAL_LIMITS.maxCompetencies) return;
    set({ competencies: [...draft.competencies, { ...BLANK_COMPETENCY, questions: [""] }] });
  };
  const removeCompetency = (i: number): void =>
    set({ competencies: draft.competencies.filter((_, idx) => idx !== i) });

  const setTopic = (i: number, value: string): void =>
    set({ protectedTopics: draft.protectedTopics.map((t, idx) => (idx === i ? value : t)) });
  const addTopic = (): void => {
    if (draft.protectedTopics.length >= VERTICAL_LIMITS.maxProtectedTopics) return;
    set({ protectedTopics: [...draft.protectedTopics, ""] });
  };
  const removeTopic = (i: number): void =>
    set({ protectedTopics: draft.protectedTopics.filter((_, idx) => idx !== i) });

  /** Move a question one slot up or down. Order is the order the fallback picker walks, so it is
   *  the owner's lever for "ask this one first" without rewriting the set. */
  const moveQuestion = (ci: number, qi: number, delta: number): void => {
    const comp = draft.competencies[ci];
    if (!comp) return;
    const to = qi + delta;
    if (to < 0 || to >= comp.questions.length) return;
    const next = [...comp.questions];
    const [moved] = next.splice(qi, 1);
    next.splice(to, 0, moved ?? "");
    setComp(ci, { questions: next });
  };

  /** Exercise on/off. OFF sends an explicit `null` on save — "clear it" on this wire, distinct
   *  from omitted (see the save payload). Switching back ON prefers what was stored before this
   *  edit session, found via the baseline: an owner who toggled off by accident gets the seeded
   *  exercise back whole — produce half included — rather than a blank that would overwrite it.
   *  Only a competency that never had one starts from the blank artefact critique. */
  const toggleExercise = (ci: number): void => {
    const comp = draft.competencies[ci];
    if (!comp) return;
    if (comp.exercise) {
      setComp(ci, { exercise: null });
      return;
    }
    const stored = draft.baseline.find((b) => b.competencyId === comp.competencyId)?.exercise;
    setComp(ci, { exercise: stored ?? { type: "artefact", critique: [{ ...BLANK_CRITIQUE }] } });
  };

  return (
    <section>
      <div className="page-h">
        <div>
          <button className="btn sm ghost" style={{ marginBottom: 8 }} onClick={onCancel}>
            ← All verticals
          </button>
          <h1>{draft.isNew ? "New vertical" : draft.name || draft.key}</h1>
          <div className="sub">
            {draft.isNew
              ? "Define the competencies an interview in this industry should probe"
              : `Currently edition v${draft.version}${draft.isSystem ? " · system vertical" : ""}`}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
          <button className="btn sm ghost" onClick={copyJson} title="Copy this vertical's name, competencies and guardrail clauses as JSON">
            <Copy /> Copy JSON
          </button>
          <button
            className="btn sm ghost"
            onClick={pasteJson}
            title="Replace the fields below with a JSON vertical from your clipboard — Copy JSON above shows the exact shape"
          >
            <ClipboardPaste /> Paste JSON
          </button>
          <button className="btn" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button className="btn primary" disabled={!canSave} onClick={onSave}>
            {saving ? "Saving…" : draft.isNew ? "Create vertical" : "Save changes"}
          </button>
        </div>
      </div>

      {jsonMsg && (
        <div className={jsonMsg.tone === "good" ? "banner info" : "banner warn"} style={{ marginBottom: 16 }}>
          {jsonMsg.tone === "good" ? <Info /> : <Alert />}
          <span>{jsonMsg.text}</span>
        </div>
      )}

      {!draft.isNew && (
        <div className={willBump ? "banner warn" : "banner info"} style={{ marginBottom: 16 }}>
          {willBump ? <Alert /> : <Info />}
          <div>
            {willBump ? (
              <>
                These changes move a weight, an anchor, a label, a practical exercise or the
                competency set, so saving
                bumps this vertical to <b>v{draft.version + 1}</b> and files the current set away as
                edition <b>v{draft.version}</b>, whole. Requisitions created before now keep reading
                that stored edition: a score only ever means what the anchors of its own edition
                said it meant, and that is what a disputed result is defended with.
              </>
            ) : (
              <>
                Still edition <b>v{draft.version}</b> — the name, description, sort order, active
                flag and fallback questions don't change what a score means, so past results stay
                comparable. Touch a weight, a label, a kind or an anchor and the next save moves it
                on by one.
              </>
            )}
          </div>
        </div>
      )}

      {draft.isSystem && (
        <div className="banner info" style={{ marginBottom: 16 }}>
          <Info />
          <div>
            <b>System vertical.</b> Re-weight it, re-word it, add competencies — all yours. It just
            can't be deleted, because it is a fallback the platform relies on. Switch it off on the
            list instead if you don't want it offered.
          </div>
        </div>
      )}

      <div className="card pad" style={{ marginBottom: 16 }}>
        <div className="grid g2">
          <div className="field">
            <label htmlFor="v-name">Name</label>
            <input
              id="v-name"
              className="input"
              value={draft.name}
              maxLength={80}
              placeholder="e.g. Nursing and allied health"
              onChange={(e) => set({ name: e.target.value })}
            />
            <div className="hint">What a recruiter sees in the "kind of role" picker.</div>
          </div>
          <div className="field">
            <label htmlFor="v-key">Key</label>
            <input
              id="v-key"
              className="input"
              value={draft.key}
              maxLength={63}
              disabled={!draft.isNew}
              placeholder="nursing_allied_health"
              style={{ fontFamily: "var(--mono)" }}
              onChange={(e) => set({ key: e.target.value })}
            />
            <div className="hint">
              {draft.isNew
                ? "Lower-case snake_case. Permanent — requisitions pin it as their audit trail."
                : "Fixed once created: requisitions pin this key, and renaming it would orphan them."}
            </div>
          </div>
        </div>

        <div className="field">
          <label htmlFor="v-desc">Description</label>
          <textarea
            id="v-desc"
            className="input"
            rows={2}
            maxLength={400}
            value={draft.description}
            placeholder="Which roles belong here, and what this vertical assumes about them."
            onChange={(e) => set({ description: e.target.value })}
          />
        </div>

        <div className="grid g2">
          <div className="field">
            <label htmlFor="v-sort">Sort order</label>
            <input
              id="v-sort"
              className="input"
              type="number"
              min={0}
              max={999}
              value={String(draft.sortOrder)}
              onChange={(e) => set({ sortOrder: Math.max(0, Math.min(999, Math.round(Number(e.target.value) || 0))) })}
            />
            <div className="hint">Lower comes first in the recruiter's picker.</div>
          </div>
          <div className="field">
            <label>Offered to recruiters</label>
            <div style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 4 }}>
              <Switch on={draft.isActive} onToggle={() => set({ isActive: !draft.isActive })} />
              <span className="mut sm">
                {draft.isActive ? "Selectable in New Request" : "Hidden — existing requisitions unaffected"}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-h">
          <h3>Competencies</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {sum !== REQUIRED_WEIGHT_SUM && (
              <button type="button" className="btn sm" onClick={evenOutWeights}>
                Even them out
              </button>
            )}
            <span className={sum === REQUIRED_WEIGHT_SUM ? "pill v" : "pill r"}>Weight sum {sum}</span>
          </div>
        </div>
        <div className="card-b">
          <p className="mut sm" style={{ margin: "0 0 14px" }}>
            Weights are <b>relative</b> — the scorer normalises them before ranking, so 40/30/30 and
            80/60/60 would score identically. They must still total{" "}
            <b>{REQUIRED_WEIGHT_SUM}</b> here, so the number you type is the share of the composite
            you get. The share on each card is what it would contribute right now.
          </p>

          {draft.competencies.map((c, i) => (
            <div
              key={i}
              className="card"
              style={{ padding: "14px 16px", marginBottom: 12, background: "var(--surface-2)" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <span className="pill n" style={{ fontSize: 12 }}>
                  {Math.round((shares[c.competencyId] ?? 0) * 100)}% of the composite
                </span>
                <span style={{ flex: 1 }} />
                <button
                  type="button"
                  className="btn sm ghost"
                  disabled={draft.competencies.length <= VERTICAL_LIMITS.minCompetencies}
                  title={
                    draft.competencies.length <= VERTICAL_LIMITS.minCompetencies
                      ? "A vertical needs at least one competency"
                      : "Remove this competency"
                  }
                  onClick={() => removeCompetency(i)}
                >
                  <Trash />
                </button>
              </div>

              <div className="grid g3">
                <div className="field">
                  <label>Label</label>
                  <input
                    className="input"
                    value={c.label}
                    maxLength={80}
                    placeholder="Classroom management"
                    onChange={(e) => setComp(i, { label: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>Id</label>
                  <input
                    className="input"
                    value={c.competencyId}
                    maxLength={63}
                    placeholder="classroom_management"
                    style={{ fontFamily: "var(--mono)" }}
                    onChange={(e) => setComp(i, { competencyId: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>Weight</label>
                  <input
                    className="input"
                    type="number"
                    min={VERTICAL_LIMITS.minWeight}
                    max={VERTICAL_LIMITS.maxWeight}
                    value={String(c.weight)}
                    onChange={(e) => setComp(i, { weight: Math.round(Number(e.target.value) || 0) })}
                  />
                </div>
              </div>

              {draft.competencies.length >= 2 && (
                <div className="field">
                  <label>Importance</label>
                  <TierToggle
                    weight={c.weight}
                    siblingWeights={draft.competencies.map((cc) => cc.weight)}
                    onChange={(tier) => setTier(i, tier)}
                  />
                </div>
              )}

              <div className="field">
                <label>Kind</label>
                <select
                  className="input"
                  value={c.kind}
                  onChange={(e) => setComp(i, { kind: e.target.value as CompetencyKind })}
                >
                  {COMPETENCY_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {KIND_LABEL[k]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>Rubric anchors</label>
                <textarea
                  className="input"
                  rows={3}
                  maxLength={1000}
                  value={c.anchors}
                  placeholder="0-40: …; 41-70: …; 71-100: …"
                  onChange={(e) => setComp(i, { anchors: e.target.value })}
                />
                <div className="hint">
                  House format <code>0-40: …; 41-70: …; 71-100: …</code>. This is the wording the
                  judge scores against and the wording a candidate is shown if they dispute a result.
                </div>
              </div>

              {/* Practical exercise — the last owner-editability gap of #18, closed. Only the
                  CRITIQUE half is authored here: `produce` (typed code against hidden test cases)
                  is RESERVED for a future typed-assessment mode and stays code-managed, so a
                  stored build spec rides through every save verbatim and the editor says so
                  rather than hiding it. */}
              <div className="field">
                <label>Practical exercise</label>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Switch on={!!c.exercise} onToggle={() => toggleExercise(i)} />
                  <span className="mut sm">
                    {c.exercise
                      ? "The candidate is shown an artefact and critiques it aloud — served once per interview at L2/L3"
                      : "No exercise — this competency is probed through spoken questions only"}
                  </span>
                </div>
                {c.exercise && (
                  <>
                    <div style={{ marginTop: 10 }}>
                      <ExerciseEditor
                        exercise={c.exercise}
                        radioGroup={`ex-choice-${i}`}
                        onChange={(ex) => setComp(i, { exercise: ex })}
                      />
                    </div>
                    <div className="hint" style={{ marginTop: 6 }}>
                      The exercise is part of what a score means — its artefact is ground truth the
                      judge reads against — so adding, editing or removing one bumps the vertical's
                      edition on save.
                    </div>
                  </>
                )}
              </div>

              <div className="field" style={{ marginBottom: 0 }}>
                <label>
                  Fallback questions{" "}
                  <span className="hint" style={{ display: "inline", fontWeight: 400 }}>
                    — asked whenever question generation is unavailable
                  </span>
                </label>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {c.questions.map((q, qi) => (
                    <div key={qi} style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                      <textarea
                        className="input"
                        rows={2}
                        maxLength={600}
                        value={q}
                        placeholder="Tell me about a time…"
                        style={{ flex: 1 }}
                        onChange={(e) =>
                          setComp(i, {
                            questions: c.questions.map((x, idx) => (idx === qi ? e.target.value : x)),
                          })
                        }
                      />
                      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <button
                          type="button"
                          className="btn sm ghost"
                          aria-label="Move question up"
                          data-tip="Move question up"
                          disabled={qi === 0}
                          onClick={() => moveQuestion(i, qi, -1)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="btn sm ghost"
                          aria-label="Move question down"
                          data-tip="Move question down"
                          disabled={qi === c.questions.length - 1}
                          onClick={() => moveQuestion(i, qi, 1)}
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="btn sm ghost"
                          aria-label="Remove question"
                          data-tip="Remove question"
                          disabled={c.questions.length <= VERTICAL_LIMITS.minQuestionsPerCompetency}
                          onClick={() =>
                            setComp(i, { questions: c.questions.filter((_, idx) => idx !== qi) })
                          }
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="btn sm"
                  style={{ marginTop: 8 }}
                  disabled={c.questions.length >= VERTICAL_LIMITS.maxQuestionsPerCompetency}
                  onClick={() => setComp(i, { questions: [...c.questions, ""] })}
                >
                  <Plus /> Add question
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            className="btn"
            disabled={draft.competencies.length >= VERTICAL_LIMITS.maxCompetencies}
            onClick={addCompetency}
          >
            <Plus /> Add competency
          </button>
        </div>
      </div>

      {/* Guardrail. Lives with the vertical rather than in code so that a vertical an owner
          creates — a care home, a faith school, a call centre — can carry the warnings its own
          interviews actually need. */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-h">
          <h3>Interview guardrail</h3>
          <span className="pill n">
            {draft.protectedTopics.filter((t) => t.trim().length > 0).length} extra clause
            {draft.protectedTopics.filter((t) => t.trim().length > 0).length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="card-b">
          <div className="banner info" style={{ marginBottom: 14 }}>
            <Info />
            <div>
              <b>Every interview already refuses protected attributes.</b> Age, gender, marital or
              family status, pregnancy, caring responsibilities, religion, caste, ethnicity, origin,
              immigration status, health, sexual orientation and political affiliation are barred in
              code, for every vertical, and can't be switched off here. Anything you add below is an
              <i> extra</i> warning for this industry specifically.
            </div>
          </div>
          <p className="mut sm" style={{ margin: "0 0 14px" }}>
            Write each one as a full instruction to the interviewer, in the second person — it is
            sent to the model verbatim, alongside the questions. The drift worth naming is the kind
            a friendly conversation in <i>this</i> trade falls into by itself: a teaching interview
            wandering onto school holidays and childcare, a shift-based one asking <i>why</i>{" "}
            someone can't work Sundays. These apply to the live follow-up too — the one question
            nobody reviews before a candidate is asked it.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {draft.protectedTopics.map((t, i) => (
              <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                <textarea
                  className="input"
                  rows={2}
                  maxLength={VERTICAL_LIMITS.maxProtectedTopicLen}
                  value={t}
                  placeholder="Do NOT ask about…"
                  style={{ flex: 1 }}
                  onChange={(e) => setTopic(i, e.target.value)}
                />
                <button
                  type="button"
                  className="btn sm ghost"
                  aria-label="Remove guardrail clause"
                  data-tip="Remove guardrail clause"
                  onClick={() => removeTopic(i)}
                >
                  <Trash />
                </button>
              </div>
            ))}
          </div>
          {draft.protectedTopics.length === 0 && (
            <p className="mut sm" style={{ margin: "0 0 12px" }}>
              No extra clauses — the universal rules above still apply.
            </p>
          )}
          <button
            type="button"
            className="btn sm"
            style={{ marginTop: draft.protectedTopics.length > 0 ? 8 : 0 }}
            disabled={draft.protectedTopics.length >= VERTICAL_LIMITS.maxProtectedTopics}
            title={
              draft.protectedTopics.length >= VERTICAL_LIMITS.maxProtectedTopics
                ? `At most ${VERTICAL_LIMITS.maxProtectedTopics} clauses — past that the model skims them`
                : undefined
            }
            onClick={addTopic}
          >
            <Plus /> Add clause
          </button>
          <div className="hint" style={{ marginTop: 10 }}>
            Editing these never bumps the vertical's edition: a guardrail changes what may be{" "}
            <b>asked</b>, not what a score means, so tightening one costs you no scoring history.
          </div>
        </div>
      </div>

      {/* Every problem at once, not the first parse failure — an owner fixing a rubric shouldn't
          have to save five times to discover five things. */}
      {problems.length > 0 && (
        <div className="banner warn" style={{ marginBottom: 16 }}>
          <Alert />
          <div>
            <b>Not ready to save yet:</b>
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {error && (
        <div className="banner crit" style={{ marginBottom: 16 }}>
          <Alert />
          <span>{error}</span>
        </div>
      )}

      <div className="card pad" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Bolt />
        <div style={{ flex: 1 }} className="mut sm">
          Serving weights can also be overridden per vertical on the{" "}
          <b>Calibration layer</b> without touching this definition — useful when a pilot says one
          competency should count for more, and you want the change reversible.
        </div>
        <button className="btn primary" disabled={!canSave} onClick={onSave}>
          {saving ? "Saving…" : draft.isNew ? "Create vertical" : "Save changes"}
        </button>
      </div>
    </section>
  );
}

/**
 * The critique half of one competency's practical exercise, editable. Every update builds a NEW
 * exercise object and hands it up through `onChange` — the draft's baseline shares object
 * references with the competency set, so an in-place mutation here would silently rewrite the
 * "before" that the edition-bump banner compares against.
 *
 * What is NOT here, on purpose:
 *   - The `produce` half. It is reserved for a future typed-assessment mode (see verticals.ts,
 *     exerciseDepthForLevels) and there is no honest way to author "hidden test cases fed to a
 *     sandbox" from a form. A stored one is preserved verbatim — the spread in every update
 *     carries it — and the one-line note below says so. It also PINS the artefact family: an
 *     `artefact` exercise carrying a CodeSpec is exactly the hand-edit resolveExercise refuses.
 *   - Blocking validation. That stays in validateVertical/exerciseErrors, which the editor's
 *     problems banner already runs over the whole draft — one list, shared copy, all at once.
 *     The only checks rendered here are the NON-blocking fairness hints (choiceLengthSpread,
 *     correctIsLongestOption): the ±15% rule is deliberately not in validateVertical, so it is
 *     deliberately not a save blocker here either.
 */
function ExerciseEditor({
  exercise,
  radioGroup,
  onChange,
}: {
  exercise: CompetencyExercise;
  /** Unique per competency — keeps each variant's correct-answer radios in their own group. */
  radioGroup: string;
  onChange: (ex: CompetencyExercise) => void;
}): ReactNode {
  const L = VERTICAL_LIMITS;
  const variants = exercise.critique ?? [];

  const setVariant = (vi: number, patch: Partial<ExerciseCritique>): void =>
    onChange({
      ...exercise,
      critique: variants.map((v, idx) => (idx === vi ? { ...v, ...patch } : v)),
    });

  const addVariant = (): void => {
    if (variants.length >= L.maxExerciseVariants) return;
    onChange({ ...exercise, critique: [...variants, { ...BLANK_CRITIQUE }] });
  };
  const removeVariant = (vi: number): void => {
    const next = variants.filter((_, idx) => idx !== vi);
    // The exercise toggle is how a WHOLE exercise is removed; this button never empties one.
    if (next.length === 0 && !exercise.produce) return;
    // An empty critique array is not a legal wire shape (the schema wants 1+ or absent), so the
    // key is dropped — reachable only when a stored produce half keeps the exercise non-empty.
    onChange({ ...exercise, critique: next.length > 0 ? next : undefined });
  };

  /** Choice on: two blank options and NO correct answer yet — the owner must point at the best
   *  one explicitly, and until then the shared validator holds the save with its both-or-neither
   *  message. Defaulting to "A is correct" would author a rubric fact nobody stated. */
  const toggleChoice = (vi: number): void => {
    const v = variants[vi];
    if (!v) return;
    setVariant(
      vi,
      v.options === undefined
        ? { options: ["", ""], correctIndex: undefined }
        : { options: undefined, correctIndex: undefined },
    );
  };
  const setOption = (vi: number, oi: number, value: string): void => {
    const v = variants[vi];
    if (!v?.options) return;
    setVariant(vi, { options: v.options.map((o, idx) => (idx === oi ? value : o)) });
  };
  const addOption = (vi: number): void => {
    const v = variants[vi];
    if (!v?.options || v.options.length >= L.maxChoiceOptions) return;
    setVariant(vi, { options: [...v.options, ""] });
  };
  const removeOption = (vi: number, oi: number): void => {
    const v = variants[vi];
    if (!v?.options || v.options.length <= L.minChoiceOptions) return;
    // Keep the mark on the same STATEMENT, not the same index — and removing the marked one
    // leaves the choice unmarked rather than silently promoting whatever slid into its slot.
    const correctIndex =
      v.correctIndex === undefined || v.correctIndex === oi
        ? undefined
        : v.correctIndex > oi
          ? v.correctIndex - 1
          : v.correctIndex;
    setVariant(vi, { options: v.options.filter((_, idx) => idx !== oi), correctIndex });
  };

  return (
    <div className="card" style={{ padding: "12px 14px" }}>
      <div className="field">
        <label>Artefact family</label>
        <select
          className="input"
          value={exercise.type}
          disabled={!!exercise.produce}
          title={
            exercise.produce
              ? "The stored build spec pins this family — build specs are code-managed"
              : undefined
          }
          onChange={(e) => onChange({ ...exercise, type: e.target.value as ExerciseType })}
        >
          {EXERCISE_TYPES.map((t) => (
            <option key={t} value={t}>
              {EXERCISE_TYPE_LABEL[t]}
            </option>
          ))}
        </select>
        <div className="hint">
          What the candidate is told they are reading — it picks the pane and the framing, never a
          code editor for an artefact.
        </div>
      </div>

      {exercise.produce && (
        <div className="hint" style={{ marginBottom: 12 }}>
          Also carries a build task — write {exercise.produce.language} against{" "}
          {exercise.produce.cases.length} hidden test case
          {exercise.produce.cases.length === 1 ? "" : "s"}. Build specs are code-managed, so it
          isn't editable here and saving keeps it exactly as stored.
        </div>
      )}

      {variants.map((v, vi) => {
        const opts = v.options;
        const filled = opts !== undefined && opts.every((o) => o.trim().length > 0);
        const spread = opts === undefined ? null : choiceLengthSpread(opts);
        const fairness = [
          ...(filled && spread && (spread.under > L.choiceLengthTolerance || spread.over > L.choiceLengthTolerance)
            ? [
                "The options differ by more than ±15% from their mean length, so length itself starts hinting at the answer. Lengthen the distractors rather than trimming the correct one.",
              ]
            : []),
          ...(filled && correctIsLongestOption(v)
            ? [
                'The correct answer is the longest option, so "pick the longest" scores without knowing the trade. Lengthen a distractor.',
              ]
            : []),
        ];
        return (
          <div
            key={vi}
            className="card"
            style={{ padding: "12px 14px", marginBottom: 10, background: "var(--surface-2)" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <span className="pill n" style={{ fontSize: 12 }}>
                Variant {vi + 1} of {variants.length} — each interview shows one, picked
                deterministically
              </span>
              <span style={{ flex: 1 }} />
              <button
                type="button"
                className="btn sm ghost"
                aria-label="Remove critique variant"
                disabled={variants.length <= 1 && !exercise.produce}
                data-tip-pos="left"
                data-tip={
                  variants.length <= 1 && !exercise.produce
                    ? "An exercise needs at least one critique variant — switch the whole exercise off instead"
                    : "Remove this variant"
                }
                onClick={() => removeVariant(vi)}
              >
                <Trash />
              </button>
            </div>

            <div className="field">
              <label>Artefact</label>
              <textarea
                className="input"
                rows={6}
                maxLength={L.maxExerciseArtefactLen}
                value={v.snippet}
                placeholder="The material the candidate reads — a code excerpt, a lesson plan, a handover note…"
                style={exercise.type === "code" ? { fontFamily: "var(--mono)", fontSize: 12.5 } : undefined}
                onChange={(e) => setVariant(vi, { snippet: e.target.value })}
              />
              <div className="hint">
                Shown to the candidate whole, in one pane — {v.snippet.length.toLocaleString()} of{" "}
                {L.maxExerciseArtefactLen.toLocaleString()} characters.
              </div>
            </div>

            <div className="field">
              <label>Question</label>
              <input
                className="input"
                value={v.prompt}
                maxLength={600}
                placeholder="What would you change before this goes out, and why?"
                onChange={(e) => setVariant(vi, { prompt: e.target.value })}
              />
              <div className="hint">
                Asked over THIS artefact — the pair travels together, and the question means nothing
                over a different one.
              </div>
            </div>

            <div className="field" style={{ marginBottom: 0 }}>
              <label>Answer options</label>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Switch on={opts !== undefined} onToggle={() => toggleChoice(vi)} />
                <span className="mut sm">
                  {opts !== undefined
                    ? "Choose-then-explain — the pick is machine-verified, then the candidate explains it aloud"
                    : "Open — the candidate critiques aloud, no options shown"}
                </span>
              </div>
              {opts && (
                <>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                    {opts.map((opt, oi) => (
                      <div key={oi} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input
                          type="radio"
                          name={`${radioGroup}-${vi}`}
                          aria-label={`Mark option ${OPTION_LETTERS[oi] ?? oi + 1} as the best answer`}
                          checked={v.correctIndex === oi}
                          onChange={() => setVariant(vi, { correctIndex: oi })}
                        />
                        <span className="mut sm" style={{ width: 14, fontWeight: 650 }}>
                          {OPTION_LETTERS[oi] ?? "?"}
                        </span>
                        <input
                          className="input"
                          style={{ flex: 1 }}
                          value={opt}
                          maxLength={L.maxChoiceOptionLen}
                          placeholder="A statement about the artefact — distractors must be genuinely false, not merely weaker"
                          onChange={(e) => setOption(vi, oi, e.target.value)}
                        />
                        <button
                          type="button"
                          className="btn sm ghost"
                          aria-label="Remove option"
                          disabled={opts.length <= L.minChoiceOptions}
                          data-tip-pos="left"
                          data-tip={
                            opts.length <= L.minChoiceOptions
                              ? `A choice needs at least ${L.minChoiceOptions} options`
                              : "Remove this option"
                          }
                          onClick={() => removeOption(vi, oi)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="btn sm"
                    style={{ marginTop: 8 }}
                    disabled={opts.length >= L.maxChoiceOptions}
                    onClick={() => addOption(vi)}
                  >
                    <Plus /> Add option
                  </button>
                  <div className="hint" style={{ marginTop: 6 }}>
                    {L.minChoiceOptions} to {L.maxChoiceOptions} statements, exactly one of them the
                    best answer — mark it with the radio. The mark is never shown to the candidate;
                    the pick is checked deterministically and handed to the judge as fact.
                  </div>
                  {v.correctIndex === undefined && (
                    <div className="hint" style={{ marginTop: 6, color: "var(--warn)" }}>
                      No best answer marked yet — a choice needs both options and a correct answer,
                      or neither, so saving waits for the radio.
                    </div>
                  )}
                  {fairness.length > 0 && (
                    <div className="hint" style={{ marginTop: 6, color: "var(--warn)" }}>
                      {fairness.map((w) => (
                        <div key={w}>{w} (A nudge, not a blocker — saving still goes through.)</div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}

      <button
        type="button"
        className="btn sm"
        disabled={variants.length >= L.maxExerciseVariants}
        title={
          variants.length >= L.maxExerciseVariants
            ? `At most ${L.maxExerciseVariants} variants per exercise`
            : undefined
        }
        onClick={addVariant}
      >
        <Plus /> Add variant
      </button>
      <div className="hint" style={{ marginTop: 8 }}>
        Up to {L.maxExerciseVariants} variants. More variants means less answer-sharing between
        candidates — each interview picks one by seed, so every variant must stand alone.
      </div>
    </div>
  );
}
